"use client";

import { useEffect, useRef } from "react";
import {
  WORKSPACE_ACTIVITY_REFRESH_EVENT,
  WRITE_QUEUE_CHANGE_EVENT,
  workspaceActivityRefreshMatches,
  type WorkspaceActivityRefreshDetail,
} from "@/lib/workspaceActivityRefresh";

const DEFAULT_POLL_INTERVAL_MS = 20_000;
const EVENT_DEBOUNCE_MS = 400;

export function useWorkspaceActivityAutoRefresh(
  workspaceId: string | null | undefined,
  refetch: () => void | Promise<void>,
  options?: { pollIntervalMs?: number },
) {
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const refetchInFlightRef = useRef(false);

  useEffect(() => {
    const ws = workspaceId?.trim() ?? "";
    if (!ws) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleRefetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (refetchInFlightRef.current) return;
        refetchInFlightRef.current = true;
        void Promise.resolve(refetchRef.current()).finally(() => {
          refetchInFlightRef.current = false;
        });
      }, EVENT_DEBOUNCE_MS);
    };

    const onRefreshEvent = (event: Event) => {
      const detail = (event as CustomEvent<WorkspaceActivityRefreshDetail>)
        .detail;
      if (!workspaceActivityRefreshMatches(ws, detail)) return;
      scheduleRefetch();
    };

    const onWriteQueueChange = (event: Event) => {
      const detail = (event as CustomEvent<{ workspaceId?: string }>).detail;
      if (!workspaceActivityRefreshMatches(ws, detail)) return;
      scheduleRefetch();
    };

    const onVisible = () => {
      if (document.hidden) return;
      scheduleRefetch();
    };

    window.addEventListener(WORKSPACE_ACTIVITY_REFRESH_EVENT, onRefreshEvent);
    window.addEventListener(WRITE_QUEUE_CHANGE_EVENT, onWriteQueueChange);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    const pollMs = options?.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    const interval = window.setInterval(() => {
      if (document.hidden || refetchInFlightRef.current) return;
      refetchInFlightRef.current = true;
      void Promise.resolve(refetchRef.current()).finally(() => {
        refetchInFlightRef.current = false;
      });
    }, pollMs);

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      window.removeEventListener(
        WORKSPACE_ACTIVITY_REFRESH_EVENT,
        onRefreshEvent,
      );
      window.removeEventListener(WRITE_QUEUE_CHANGE_EVENT, onWriteQueueChange);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.clearInterval(interval);
    };
  }, [workspaceId, options?.pollIntervalMs]);
}
